import { useEffect, useState } from 'react'

import { ProblemAlert } from '@/components/error-surface'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import type { ApiError } from '@/lib/problem-details'

/**
 * A command whose whole payload is a reason.
 *
 * Refusing an application and calling a round off are the two operations on a round that key no
 * amount, move no money and are not countersigned — so they stay dialogs on the round itself
 * rather than becoming screens that refuse a phone. A branch manager turning down an application
 * from the road is a thing the system should allow; releasing money from the road is not.
 */
export function ReasonDialog({
  open,
  onOpenChange,
  title,
  description,
  label,
  placeholder,
  hint,
  confirmLabel,
  tone = 'default',
  isPending = false,
  error = null,
  onConfirm,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description: string
  label: string
  placeholder?: string
  hint?: string
  confirmLabel: string
  tone?: 'default' | 'danger'
  isPending?: boolean
  error?: ApiError | null
  onConfirm: (reason: string) => void
}) {
  const [reason, setReason] = useState('')

  useEffect(() => {
    if (!open) {
      setReason('')
    }
  }, [open])

  const trimmed = reason.trim()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="loan-reason">{label}</Label>
            <textarea
              id="loan-reason"
              rows={3}
              autoFocus
              value={reason}
              placeholder={placeholder}
              onChange={(event) => setReason(event.target.value)}
              aria-describedby={hint ? 'loan-reason-hint' : undefined}
              className="w-full rounded-control border border-border-subtle bg-surface px-3 py-2 text-sm text-heading outline-none transition-colors placeholder:text-muted focus:border-brand-text hover:border-border-strong"
            />
            {hint ? (
              <p id="loan-reason-hint" className="text-xs text-muted">
                {hint}
              </p>
            ) : null}
          </div>

          {error ? <ProblemAlert error={error} /> : null}
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button
            variant={tone === 'danger' ? 'danger' : 'primary'}
            disabled={trimmed === '' || isPending}
            onClick={() => onConfirm(trimmed)}
          >
            {isPending ? 'Working…' : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
